import { Button, Dialog, DialogActions, DialogContent } from '@mui/material'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import RadioButtons from 'components/forms/RadioButtons'
import moment from 'moment'
import { getYear } from 'utils/DateUtil'
import { hasValue } from 'utils/NumberUtil'
import { nbPerPageLabelMedium } from 'referencial/constants/ReferencialConstants'
import AgriAction from 'agriAdministration/actions/AgriAction'
import { getTextSlot, setTableIndex } from 'exploitations/utils/AgriUtils'
import { useParams } from 'react-router-dom'
import { CardTable } from 'components/datatable/NewTable'
import { darkBlue } from 'utils/constants/ColorTheme'

const headers = ['samplerName', 'pumpType', 'material', 'serialNumber', 'nature', 'identifier']

const AddExploitationWaterTurnsDialog = ({
    openAdd = false,
    setOpenAdd = () => { },
    addWaterTurns = () => { },
}) => {
    const dispatch = useDispatch()
    const { id } = useParams()

    const {
        waterTurnsSlots,
    } = useSelector(store => ({
        waterTurnsSlots: store.AgriReducer.waterTurnsSlots,
    }), shallowEqual)

    const [exploitationsToAdd, setExploitationsToAdd] = useState([])
    const [selectedSlot, setSelectedSlot] = useState()

    const onClose = () => {
        setExploitationsToAdd([])
        setSelectedSlot()
        setOpenAdd(false)
    }

    const nowYear = useMemo(() => getYear(moment.now()), [])

    const radioSlots = useMemo(() => {
        return waterTurnsSlots.filter(d => d.year === nowYear).map(d => {
            return {
                ...d,
                name: getTextSlot(d),
            }
        })
    }, [waterTurnsSlots])

    useEffect(() => {
        if (openAdd) {
            setSelectedSlot(waterTurnsSlots.filter(d => d.year === nowYear)?.[0]?.id)
        }
    }, [openAdd])

    const actions = [{
        icon: 'add',
        tooltip: i18n.add,
        color: 'white',
        'data-cy': 'add_tmp_exp_water_turns_button',
        onClick: () => {
            if (hasValue(selectedSlot)) {
                AgriAction.addWaterTurns({ idExploitations: [parseInt(id)], idSlot: selectedSlot }).then(json => {
                    setExploitationsToAdd(setTableIndex([...exploitationsToAdd, ...json]))
                })
            } else {
                dispatch(ToastrAction.warning(i18n.pleaseCompleteAllRequiredField))
            }
        },
    }]

    return (
        <Dialog
            maxWidth='lg'
            open={openAdd}
            onClose={onClose}
            data-cy='dialog_add_exp_water_turns'
        >
            <DefaultDialogTitle
                title={i18n.addWaterTurns}
                onClose={onClose}
            />
            <DialogContent>
                <StyledFieldSet style={{ marginTop: 0, marginBottom: '10px' }}>
                    <StyledLegend className='padding-left-1 padding-right-1'>{i18n.slots}*</StyledLegend>
                    <RadioButtons
                        col={12}
                        className='no-padding no-margin'
                        elements={radioSlots}
                        selected={selectedSlot}
                        onChange={setSelectedSlot}
                    />
                </StyledFieldSet>
                <CardTable
                    title={i18n.waterTurns}
                    actions={actions}
                    rows={exploitationsToAdd}
                    headers={headers}
                    rowsPerPageOptions={nbPerPageLabelMedium}
                    lineActions={[{
                        icon: 'clear',
                        onClick: v => setExploitationsToAdd(exploitationsToAdd.filter(ead => ead.index !== v.index)),
                    }]}
                    color={darkBlue}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    variant='contained'
                    onClick={() => {
                        if (exploitationsToAdd.length) {
                            addWaterTurns(exploitationsToAdd)
                            setExploitationsToAdd([])
                            setSelectedSlot()
                            setOpenAdd(false)
                        } else {
                            dispatch(ToastrAction.warning(i18n.nothingToDoOrProcess))
                        }
                    }}
                    data-cy='add_exp_water_turns_button'
                >
                    {i18n.add}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

AddExploitationWaterTurnsDialog.propTypes = {
    openAdd: PropTypes.bool,
    setOpenAdd: PropTypes.func,
    addWaterTurns: PropTypes.func,
}

export default AddExploitationWaterTurnsDialog